import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';

/**
 * Generic reusable confirmation modal for destructive actions.
 * Introduced in iter132.2 because Delete Document used to fire immediately
 * (no confirm), which cost customers real data on mis-clicks.
 *
 * Props:
 *   open, onOpenChange   - Dialog state (parent-owned)
 *   title                - Bold heading, e.g. "Delete this document?"
 *   description          - Sub-text; can be a string or JSX (e.g. show the
 *                          item name in bold)
 *   confirmLabel         - Button label, default "Delete"
 *   confirmTone          - "danger" | "warning" | "primary" — controls
 *                          button colour. Default "danger" (red).
 *   busy                 - Show spinner + disable confirm while an async
 *                          delete is in flight
 *   onConfirm            - Called when the user confirms
 *   testIdPrefix         - Optional prefix so multiple confirms can co-
 *                          exist on the same page. Defaults to "delete".
 */
const toneStyles = {
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  warning: 'bg-amber-600 hover:bg-amber-700 text-white',
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
};

export const DeleteConfirmDialog = ({
  open,
  onOpenChange,
  title = 'Delete?',
  description = 'This action cannot be undone.',
  confirmLabel = 'Delete',
  confirmTone = 'danger',
  busy = false,
  onConfirm,
  testIdPrefix = 'delete',
}) => {
  const iconColor = confirmTone === 'danger'
    ? 'bg-red-100 text-red-700'
    : confirmTone === 'warning'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-blue-100 text-blue-700';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-md" data-testid={`${testIdPrefix}-confirm-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <span className={`inline-flex w-8 h-8 items-center justify-center rounded-full ${iconColor}`}>
              <AlertTriangle className="w-4 h-4" />
            </span>
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-600 pt-1">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-300 text-slate-700"
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className={toneStyles[confirmTone] || toneStyles.danger}
            data-testid={`${testIdPrefix}-confirm-btn`}
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Trash2 className="w-4 h-4 mr-2" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
